import { M2MScope } from '../../generated/graphql';
import { IIsoCountryCode } from '../../sdk/user';
import { Trie } from '../../structures/trie';

export interface IM2MUser {
  clientId: string;
  scopes: string[];
  isM2M: true;
  // OPTIONAL: only for compatibility with ISupportUser interface. Not required for M2M users.
  userName: string;
  email: string;
  roles: Role[]; // Always empty array for M2M users
}

export interface ISupportUser {
  email: string;
  roles?: Role[];
  userName: string;
  isM2M: false;
  scopes: string[]; // Always empty array for human users
}

export const businessRoles = ['supervisor', 'agent', 'bot'];
export const databaseRoles = ['can-admin', 'usa-admin'] as const;

export const allRoles = [...databaseRoles, ...businessRoles];

export const limits = ['points', 'refundAmount', 'refundDays'] as const;
export type Role = (typeof allRoles)[number];
export type LimitType = (typeof limits)[number];

/* eslint-disable no-shadow */
export enum Permission {
  MergeBalance = 'RBI.supportActions.giftCard.merge',
  ReplaceBalance = 'RBI.supportActions.giftCard.replaceBalance',
  IssueLoyaltyPoints = 'RBI.supportActions.loyalty.issuePoints',
  RefundOrders = 'RBI.supportActions.orders.refund',
  CreateAccount = 'RBI.supportActions.accounts.create',
  AuditAccount = 'RBI.supportActions.accounts.auditAccount',
  //Database Superpermission
  ReadDB = 'RBI.database.read',
  WriteDB = 'RBI.database.write',
  //Region Subpermissions
  ReadCanDB = 'RBI.database.read.can',
  ReadUsaDB = 'RBI.database.read.usa',
  WriteCanDB = 'RBI.database.write.can',
  WriteUsaDB = 'RBI.database.write.usa',
}

/**
 * Maps GraphQL M2MScope enum values to their actual OAuth2 scope strings.
 */
export const M2M_SCOPE_VALUES: Record<M2MScope, string> = {
  [M2MScope.LOYALTY_ISSUE_POINTS]: 'RBI.supportActions.loyalty.issuePoints',
  [M2MScope.LOYALTY_DEDUCT_POINTS]: 'RBI.supportActions.loyalty.deductPoints',
  [M2MScope.LOYALTY_CREATE_PERSONALIZED_OFFER]:
    'RBI.supportActions.loyalty.createPersonalizedOffer',
  [M2MScope.LOYALTY_READ_USER_INFO]: 'RBI.supportActions.loyalty.readUserInfo',
  [M2MScope.LOYALTY_READ_TRANSACTIONS]: 'RBI.supportActions.loyalty.readTransactions',
  [M2MScope.LOYALTY_READ_OFFERS]: 'RBI.supportActions.loyalty.readOffers',
  [M2MScope.LOYALTY_READ_QUESTS]: 'RBI.supportActions.loyalty.readQuests',
  [M2MScope.LOYALTY_MANAGE_SECONDARY_IDENTIFIERS]:
    'RBI.supportActions.loyalty.manageSecondaryIdentifiers',
  [M2MScope.LOYALTY_MANAGE_PARTNERSHIP]: 'RBI.supportActions.loyalty.managePartnership',
  [M2MScope.CUSTOMER_READ]: 'RBI.supportActions.customer.read',
  [M2MScope.CUSTOMER_CREATE]: 'RBI.supportActions.customer.create',
  [M2MScope.CUSTOMER_UPDATE]: 'RBI.supportActions.customer.update',
  [M2MScope.ORDERS_READ]: 'RBI.supportActions.orders.read',
  [M2MScope.ORDERS_REMEDIATE]: 'RBI.supportActions.orders.remediate',
  [M2MScope.GIFT_CARD_READ]: 'RBI.supportActions.giftCard.read',
  [M2MScope.GIFT_CARD_UPDATE]: 'RBI.supportActions.giftCard.update',
  [M2MScope.STORE_READ]: 'RBI.supportActions.store.read',
  [M2MScope.DSAR_READ]: 'RBI.supportActions.dsar.read',
  [M2MScope.LAUNCH_DARKLY_READ]: 'RBI.supportActions.launchDarkly.read',
  [M2MScope.US_ADMIN]: 'RBI.supportActions.usAdmin',
  [M2MScope.CA_ADMIN]: 'RBI.supportActions.caAdmin',
};

/**
 * Converts a GraphQL M2MScope enum value to its corresponding OAuth2 scope string.
 * @param scope - The M2MScope enum value from GraphQL
 * @returns The actual OAuth2 scope string used in tokens
 */
export function getM2MScopeValue(scope: M2MScope): string {
  return M2M_SCOPE_VALUES[scope];
}

/* eslint-disable sort-keys */
export const rolePermissionMapping: Record<Role, string[]> = {
  bot: ['RBI.supportActions.loyalty.issuePoints', 'RBI.supportActions.orders.refund'],
  agent: ['RBI.supportActions.orders.*', 'RBI.supportActions.loyalty.*'],
  supervisor: ['RBI.supportActions.*'],
  //Database Roles
  'can-admin': ['RBI.database.read.can', 'RBI.database.write.can'],
  'usa-admin': ['RBI.database.read.usa', 'RBI.database.write.usa'],
};

//Limitation mapping, infinity means no limit
const roleLimitMapping: Record<Role, Record<LimitType, number>> = {
  agent: { points: Infinity, refundAmount: Infinity, refundDays: 30 },
  bot: { points: Infinity, refundAmount: Infinity, refundDays: 30 },
  supervisor: { points: Infinity, refundAmount: Infinity, refundDays: Infinity },
};

const permissionTrie = new Trie();

//Flattens the permissions to one flat list containing all user permissions.
export function getUserPermissions(roles: Role[]): string[] {
  const hasNoBusinessRoles = !roles.find((role) => businessRoles.includes(role));
  if (hasNoBusinessRoles) {
    // default role is bot if user has no business roles assigned.
    roles.push('bot');
  }

  permissionTrie.addAll(Object.values(Permission));
  let userPermissions: string[] = [];
  roles?.forEach((role) => {
    //Add all permissions from group to user
    rolePermissionMapping[role]?.forEach((permission) => {
      userPermissions = [
        ...userPermissions,
        ...permissionTrie.search(permission.replace('.*', '')),
      ];
    });
  });
  return [...new Set(userPermissions)];
}

//Gets the maximum limitations for a given user.
export function getUserLimits(roles: Role[]): Record<LimitType, number> {
  const userLimits: Record<LimitType, number> = { points: 0, refundAmount: 0, refundDays: 0 };
  for (const role of roles) {
    if (businessRoles.includes(role)) {
      for (const limit in roleLimitMapping[role] ?? []) {
        if (limit in userLimits) {
          userLimits[limit as LimitType] = Math.max(
            userLimits[limit as LimitType],
            roleLimitMapping[role][limit as LimitType],
          );
        }
      }
    }
  }
  return userLimits;
}

//Populates all the roles into a Trie object.
export function getPermissionTrie(): Trie {
  permissionTrie.addAll(Object.values(Permission));
  return permissionTrie;
}

/**
 * Check if a user (human or M2M) has permission for a specific region.
 * For human users: validates roles (e.g., 'usa-admin', 'can-admin')
 * For M2M users: validates regional scopes (e.g., 'RBI.supportActions.usAdmin', 'RBI.supportActions.caAdmin')
 *
 * @param {ISupportUser | IM2MUser} user - The user to check (human or M2M)
 * @param {IIsoCountryCode} region - The region to check permission for
 * @returns {boolean} True if the user has permission for the region
 */
export function hasRegionalPermission({
  user,
  region,
}: {
  user: ISupportUser | IM2MUser;
  region: IIsoCountryCode;
}): boolean {
  // M2M user
  if (user.isM2M) {
    const scopes = user.scopes ?? [];
    const regionLower = region.toLowerCase();

    // Map region codes to M2M regional scopes
    const regionToScope: Record<string, M2MScope> = {
      us: M2MScope.US_ADMIN,
      usa: M2MScope.US_ADMIN,
      ca: M2MScope.CA_ADMIN,
      can: M2MScope.CA_ADMIN,
    };

    const requiredScope = regionToScope[regionLower];
    if (!requiredScope) {
      // Unknown region, deny access
      return false;
    }
    // Convert emum to actual scope string for comparison
    const requiredScopeValue = getM2MScopeValue(requiredScope);
    return scopes.includes(requiredScopeValue);
  }

  // Human user validation
  const userPermissions = getUserPermissions(user?.roles ?? []);
  return userPermissions.includes(Permission.ReadDB.valueOf() + `.${region.toLowerCase()}`);
}
